var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import React, { useContext, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';
import classnames from 'classnames';
import { isEqual } from 'lodash';
import { Popover, Transition } from '@headlessui/react';
import accounting from 'accounting';
import moment from 'moment';
import { titleize } from 'underscore.string';
import { convertDistance } from 'helpers/locations';
import { getClassName } from 'utils/ui/classes';
import Pagination from 'components/v2/Pagination';
import CargomaticPane from 'components/v2/pane';
import Badge from 'components/v2/Badge';
import { getMarkets } from 'utils/MarketAPIUtils';
import { getCarrierOffersV2 } from 'utils/MarketplaceAPI';
import { getAuthorizedDrivers } from 'utils/CarrierDriverWebAPIUtils';
import { withGoogleMap, GoogleMap, Marker } from 'react-google-maps';
import useInterval from 'hooks/useInterval';
import usePageVisibility from 'hooks/usePageVisibility';
import SessionStore from 'stores/SessionStore';
import { showToast } from 'stores/ToastStore';
import { useTranslation, Translation } from 'react-i18next';
import { DistanceUnitFormatter, LoadDimensionFormatter, LoadUnitFormatter, } from 'components/modules/master/formatters';
import formatMoney from 'utils/formatters/Money';
import { LocaleContext } from 'providers/locale-settings-context';
import { sluggify } from 'utils/localization/utils';
import ArrowRightIcon from '../v2/icons/ArrowRightIcon';
import CircleButton from '../v2/buttons/CircleButton';
import DocumentIcon from '../v2/icons/DocumentIcon';
import Multiselect from '../v2/multi-select';
import CustomerSearch from '../site/customer-search';
import Alert from '../ui/Alert';
import { isIntermodalShipmentType } from '../../helpers/shipment-types';
import OfferDetails from './OfferDetails';
const REFRESH_INTERVAL = 20000;
const PER_PAGE = 10;
const DATE_FORMAT = 'L';
const defaultFilters = [
    {
        key: 'market',
        localizationSlug: 'MARKET',
        type: 'multiselect',
        options: [],
        count: 1,
    },
    {
        key: 'order.service_type',
        localizationSlug: 'TYPE',
        type: 'multiselect',
        options: [
            {
                value: 'drop',
                localizationSlug: 'DROP_HOOK',
            },
            {
                value: 'live',
                localizationSlug: 'LIVE_LOAD_UNLOAD',
            },
        ],
    },
    {
        key: 'shipment_type',
        localizationSlug: 'MODE',
        type: 'multiselect',
        options: [
            {
                value: 'shorthaul',
                localizationSlug: 'SHORTHAUL',
            },
            {
                value: 'drayage',
                localizationSlug: 'DRAYAGE',
            },
        ],
    },
];
const filterButtonBaseClasses = 'relative px-4 py-3 font-medium focus:z-10 focus:outline-none focus:ring-1 focus:ring-green focus:border-green border border-gray-100 bg-white w-full sm:w-auto';
const filterBarClassName = 'relative z-10 flex justify-center flex-wrap sm:items-center sm:justify-start sm:flex-row mb-4 sm:mb-0';
const FilterBar = ({ filters, filterMetadata, className, onFilterChange }) => {
    const { t } = useTranslation();
    const lastIndex = filters.length - 1;
    function handleClearAutocomplete(key) {
        return () => {
            onFilterChange(key, { name: '', _id: '' });
        };
    }
    function handleAutocompleteSelect(key) {
        return (_text, item) => {
            onFilterChange(key, item);
        };
    }
    function handleSelectItems(key) {
        return (items) => {
            onFilterChange(key, items.map((item) => item.value));
        };
    }
    function handleChange(key) {
        return (value) => {
            onFilterChange(key, value);
        };
    }
    return (React.createElement("div", { className: getClassName(filterBarClassName, className) }, filters.map((filter, i) => {
        const { key, label, placeholder, type, customerType, options, localizationSlug, value, } = filter;
        let hasValue;
        if (type === 'multiselect' && options.find((o) => o.selected)) {
            hasValue = true;
        }
        else if (type === 'customer') {
            hasValue = !!filterMetadata[key]._id;
        }
        else if (type === 'range') {
            hasValue = (value || []).length > 0;
        }
        else {
            hasValue = !!value;
        }
        const buttonClassName = getClassName(filterButtonBaseClasses, {
            'xl:rounded-l-full': i === 0,
            'xl:rounded-r-full': i === lastIndex,
            'text-gray-700': !hasValue,
            'text-green-700': hasValue,
            'bg-green-100': hasValue,
            'hover:bg-gray-50': !hasValue,
        });
        return (React.createElement("div", { key: key },
            React.createElement(Popover, { className: "relative" }, ({ open }) => (React.createElement(React.Fragment, null,
                React.createElement(Popover.Button, { className: buttonClassName }, titleize(t(filter.localizationSlug, { count: filter.count }))),
                React.createElement(Popover.Overlay, { className: classnames({
                        'bg-black': true,
                        'opacity-30': open,
                        fixed: open,
                        'inset-0': open,
                        'opacity-0': !open,
                    }) }),
                React.createElement(Transition, { show: open, enter: "transition ease-out duration-200", enterFrom: "opacity-0 translate-y-1", enterTo: "opacity-100 translate-y-0", leave: "transition ease-in duration-150", leaveFrom: "opacity-100 translate-y-0", leaveTo: "opacity-0 translate-y-1" },
                    React.createElement(Popover.Panel, { static: true, className: "absolute left-0 z-10 w-screen h-auto max-w-sm px-4 mt-3 sm:px-0" },
                        React.createElement("div", { className: "rounded-lg shadow-lg ring-1 ring-black ring-opacity-5" },
                            React.createElement("div", { className: "relative bg-white p-7" },
                                React.createElement("div", { className: "flex items-center mb-2 space-x-2" },
                                    React.createElement("h3", { className: "my-0" }, titleize(t(localizationSlug, { count: filter.count }))),
                                    value && (React.createElement("p", { className: "my-0 text-sm text-gray-700" },
                                        formatMoney(value[0]),
                                        " -",
                                        ' ',
                                        formatMoney(value[1])))),
                                type === 'multiselect' && (React.createElement(Multiselect, { label: t('SELECT_WITH_VALUE', {
                                        name: titleize(t(localizationSlug, { count: filter.count })),
                                    }), items: options, onSelect: handleSelectItems(key), valueKey: "value", searchKey: "name" })),
                                type === 'customer' && (React.createElement("div", null,
                                    !filterMetadata[key]._id && (React.createElement("div", null,
                                        React.createElement("label", { className: "block mb-2 form-label" }, label),
                                        React.createElement(CustomerSearch, { onSelect: handleAutocompleteSelect(key), placeholder: t(placeholder), authorizedOnly: true, type: customerType }))),
                                    filterMetadata[key]._id && (React.createElement(Badge, { withRemove: true, color: "green", onRemove: handleClearAutocomplete(key) }, t(filterMetadata[key].localizationSlug))))))))))))));
    })));
};
FilterBar.propTypes = {
    filters: PropTypes.arrayOf(PropTypes.shape({
        key: PropTypes.string,
    })),
    onFilterChange: PropTypes.func.isRequired,
};
FilterBar.defaultProps = {
    filters: [],
};
const cardHeadersClassName = 'flex flex-wrap justify-between px-6 py-2 bg-gray-100 border-b sticky top-0 text-center';
const CardHeaders = () => {
    const { t } = useTranslation();
    return (React.createElement("div", { className: cardHeadersClassName },
        React.createElement("div", { className: "flex -mx-2" },
            React.createElement("div", { className: "px-2" },
                React.createElement("p", { className: "w-32 px-2 text-xs font-semibold text-left" }, t('ORIGIN').toUpperCase())),
            React.createElement("div", { className: "px-2" },
                React.createElement("div", { className: "w-5" }, "\u00A0")),
            React.createElement("div", { className: "px-2" },
                React.createElement("p", { className: "w-32 px-2 text-xs font-semibold text-right" }, t('DESTINATION').toUpperCase()))),
        React.createElement("div", { className: "px-2" },
            React.createElement("p", { className: "w-40 text-xs font-semibold text-left" }, t('SHIPMENT', { count: 1 }).toUpperCase())),
        React.createElement("div", { className: "px-2" },
            React.createElement("p", { className: "w-20 text-xs font-semibold" }, t('DISTANCE').toUpperCase())),
        React.createElement("div", { className: "px-2" },
            React.createElement("p", { className: "w-24 text-xs font-semibold" }, t('EARNING').toUpperCase())),
        React.createElement("div", { className: "px-2 items-end" },
            React.createElement("div", { className: "w-32" }))));
};
const cardClassName = 'flex flex-wrap justify-between p-6 cursor-pointer group hover:bg-gray-50 border-b';
const Card = ({ offer, selected, accessorialsLink, onClick, onDetailsClick, }) => {
    const { t } = useTranslation();
    function handleDocumentClick() {
        if (accessorialsLink) {
            window.open(accessorialsLink, 'blank').focus();
        }
    }
    function handleClick() {
        onClick(offer);
    }
    function handleDetailsButtonClick() {
        onClick(offer);
        onDetailsClick();
    }
    const { localeData } = useContext(LocaleContext);
    function formatRowDate(date, timezone) {
        const { t } = useTranslation();
        if (!date) {
            return t('NOT_SET');
        }
        const momentDate = timezone ? moment.tz(date, timezone) : moment(date);
        return momentDate.format('L LT');
    }
    function getServiceType(slug) {
        if (slug === 'live') {
            return t('LIVE_LOAD_UNLOAD');
        }
        return t('DROP_HOOK');
    }
    const origin = (offer.origin || [])[0] || {};
    const destination = (offer.destination || [])[0] || {};
    const intermodalShipmentType = isIntermodalShipmentType(offer);
    let equipmentStr;
    if (!intermodalShipmentType) {
        equipmentStr = (offer.equipment_type || []).map((eq) => t(eq)).join(', ');
    }
    const originDisplay = `${origin.city}, ${origin.state}`;
    const destinationDisplay = `${destination.city}, ${destination.state}`;
    const stops = offer.stops || [];
    const stopEquipments = stops.length > 0 ? stops[0].equipments || [] : [];
    const hasStopEquipment = stopEquipments.length > 0 && stopEquipments[0];
    const pickupTimezone = origin.location_timezone || origin.market.timezone;
    const deliverTimezone = destination.location_timezone || destination.market.timezone;
    return (React.createElement("div", { className: getClassName(cardClassName, {
            'bg-white': !selected,
            'bg-gray-50': selected,
        }), onClick: handleClick },
        React.createElement("div", { className: "flex items-center justify-between w-full mb-4 sm:-mx-2 sm:mb-0 sm:w-auto sm:justify-start" },
            React.createElement("div", { className: "sm:px-2" },
                React.createElement("div", { className: "w-24 mb-1 text-sm font-medium text-gray-700 truncate lg:w-32", title: originDisplay }, originDisplay),
                intermodalShipmentType && (React.createElement("div", { className: "" },
                    offer.estimated_arrival && (React.createElement("div", { className: "text-sm text-gray-500" }, t('ETA_DATE', {
                        date: moment(offer.estimated_arrival).format(DATE_FORMAT),
                    }))),
                    offer.last_cut_date && (React.createElement("div", { className: "text-sm text-gray-500" }, t('LCD_DATE', {
                        date: moment(offer.last_cut_date).format(DATE_FORMAT),
                    }))),
                    offer.last_free_date && (React.createElement("div", { className: "text-sm text-gray-500" }, t('LFD_DATE', {
                        date: moment(offer.last_free_date).format(DATE_FORMAT),
                    }))))),
                !intermodalShipmentType && (React.createElement("div", null,
                    React.createElement("div", { className: "text-sm text-gray-500" }, formatRowDate(offer.pickup_by, pickupTimezone))))),
            React.createElement("div", { className: "sm:px-2" },
                React.createElement(ArrowRightIcon, { className: "w-5 h-5" })),
            React.createElement("div", { className: "sm:px-2" },
                React.createElement("div", { className: "w-24 mb-1 text-sm font-medium text-gray-700 truncate lg:w-32", title: destinationDisplay }, destinationDisplay),
                !intermodalShipmentType && (React.createElement("div", null,
                    React.createElement("div", { className: "text-sm text-gray-500" }, formatRowDate(offer.deliver_by, deliverTimezone)))),
                intermodalShipmentType && offer.order.service_type && (React.createElement("div", { className: "text-gray-500" }, getServiceType(offer.order.service_type))))),
        React.createElement("div", { className: "sm:px-2" },
            intermodalShipmentType && hasStopEquipment && (React.createElement("div", { className: "w-40 mb-1 text-sm font-medium text-gray-700 truncate" },
                t(sluggify(stopEquipments[0]._type && stopEquipments[0]._type)) +
                    ' ',
                stopEquipments[0]._equipmentSize,
                "'")),
            !intermodalShipmentType && (React.createElement("div", { className: "mb-1 text-sm" },
                React.createElement("p", { className: "w-40 mb-1 font-medium text-gray-700 truncate", title: equipmentStr }, equipmentStr),
                offer.loads &&
                    offer.loads.length &&
                    offer.loads.map((load, i) => (React.createElement("p", { className: "w-40 mb-1 text-gray-500 truncate", key: i }, formatLoad(load)))))),
            React.createElement("div", { className: "w-40 text-gray-500 truncate" },
                React.createElement(LoadUnitFormatter, { weight: accounting.formatNumber(offer.weight), weight_units: offer.weight_units }))),
        React.createElement("div", { className: "sm:px-2 text-center" },
            React.createElement("div", { className: "w-20" },
                React.createElement("div", { className: "mb-1 text-sm font-medium text-gray-700" },
                    React.createElement(DistanceUnitFormatter, { toUnit: localeData.distance_units, distance: accounting.formatNumber(offer.distance) })))),
        React.createElement("div", { className: "sm:px-2 text-center" },
            React.createElement("div", { className: "mb-1 text-lg font-medium text-gray-700 w-24" }, formatMoney(offer.rate ? offer.rate : offer.price || 0, offer === null || offer === void 0 ? void 0 : offer.currency)),
            offer.rate_per_distance !== 0 && (React.createElement("div", { className: "text-gray-500" }, t('UNIT_PER_DISTANCE', {
                ratePerMile: formatMoney(offer.rate_per_distance, offer === null || offer === void 0 ? void 0 : offer.currency),
                distanceUnit: localeData.distance_units,
            })))),
        React.createElement("div", { className: "flex flex-col items-end" },
            React.createElement("div", { className: "px-2 mb-2 text-xs" }, formatTimeAgo(offer.exposed_at)),
            React.createElement("div", { className: "flex items-center justify-end w-full mt-4 sm:px-2 actions space-x-2 sm:w-auto sm:mt-0" },
                React.createElement(CircleButton, { color: "blue", onClick: handleDocumentClick },
                    React.createElement(DocumentIcon, { className: "w-5 h-5" })),
                React.createElement("div", null,
                    React.createElement("button", { type: "button", className: "button button-green details-button", onClick: handleDetailsButtonClick },
                        React.createElement(Translation, null, (t, { i18n }) => React.createElement(React.Fragment, null, titleize(t('DETAIL', { count: 2 }))))))))));
};
const Map = withGoogleMap(({ offer, defaultZoom, defaultCenter, style }) => {
    let markers = [];
    let center;
    if (offer) {
        const { origin, destination } = offer;
        markers = [origin, destination].map((location, i) => {
            return React.createElement(Marker, { key: i, position: location[0] });
        });
        const originLocation = origin[0];
        const { lat, lng } = originLocation;
        center = {
            lat,
            lng,
        };
    }
    else {
        center = Object.assign({}, defaultCenter);
    }
    return (React.createElement(GoogleMap, { defaultZoom: defaultZoom, defaultCenter: defaultCenter, center: center, style: style }, markers.length > 0 && markers));
});
Map.propTypes = {
    style: PropTypes.object,
    defaultZoom: PropTypes.number,
    defaultCenter: PropTypes.shape({
        lat: PropTypes.number,
        lng: PropTypes.number,
    }),
};
Map.defaultProps = {
    style: {
        width: '100%',
        height: '100%',
    },
    defaultZoom: 10,
    defaultCenter: {
        lat: 33.76744,
        lng: -118.19066,
    },
};
const LoadBoard = () => {
    const { t } = useTranslation();
    const { localeData } = useContext(LocaleContext);
    const defaultCenterForMap = { lat: 33.76744, lng: -118.19066 };
    const sortingOptions = [
        {
            value: 'exposed_at__desc',
            localization_name: 'AGE_LOWEST_TO_HIGHEST',
        },
        {
            value: 'exposed_at__asc',
            localization_name: 'AGE_HIGHEST_TO_LOWEST',
        },
        /*
        {
          value: 'estimated_arrival__asc',
          name: 'ETA Earliest to Latest',
        },
        {
          value: 'estimated_arrival__desc',
          name: 'ETA Latest to Earliest',
        },
        */
    ];
    const [carrier, setCarrier] = useState(null);
    const [admin, setAdmin] = useState(null);
    const [drivers, setDrivers] = useState([]);
    const [acceptOfferDisabled, setAcceptOfferDisabled] = useState(false);
    const [sortValue, setSortValue] = useState('exposed_at__asc');
    const [page, setPage] = useState(1);
    const [pages, setPages] = useState(1);
    const [total, setTotal] = useState(0);
    const [currentOffers, setCurrentOffers] = useState([]);
    const [offersLoaded, setOffersLoaded] = useState(false);
    const [offersLoadError, setOffersLoadError] = useState(false);
    const [selectedOffer, setSelectedOffer] = useState(null);
    const [offersLoadedFirstTime, setOffersLoadedFirstTime] = useState(false);
    const [isPaneOpen, setIsPaneOpen] = useState(null);
    const [openModal, setOpenModal] = useState(false);
    const [shorthaulAccessorialsLink, setShorthaulAccessorialsLink] = useState('');
    const [drayageAccessorialsLink, setDrayageAccessorialsLink] = useState('');
    const [filters, setFilters] = useState([...defaultFilters]);
    const [filterMetadata, setFilterMetadata] = useState({
        market: [],
        // price: [700, 900],
        carrier: {
            name: '',
            _id: '',
        },
        shipment_type: [],
        'order.service_type': [],
    });
    const isVisible = usePageVisibility(true);
    const [cancelTokenSource, setCancelTokenSource] = useState();
    function cancelRequest() {
        if (cancelTokenSource) {
            cancelTokenSource.cancel();
        }
    }
    useEffect(() => {
        return () => {
            cancelRequest();
        };
    }, []);
    useEffect(() => {
        if (!isVisible) {
            cancelRequest();
        }
    }, []);
    function handleResetOffers() {
        setOffersLoaded(false);
        setOffersLoadError(false);
        setCurrentOffers([]);
    }
    function handleFetchOffersRequested() {
        setOffersLoaded(false);
        setOffersLoadError(false);
    }
    function handleFetchOffersSuccess() {
        setOffersLoaded(true);
        setOffersLoadError(false);
    }
    function handleFetchOffersFail() {
        setOffersLoaded(true);
        setOffersLoadError(true);
    }
    async function fetchAccessorialLinks() {
        try {
            setShorthaulAccessorialsLink(`${CDN_URL}/assets/carrier-accessorials-2017.pdf`);
            setDrayageAccessorialsLink(`${CDN_URL}/assets/drayage-accessorials-2021.pdf`);
        }
        catch (e) {
            console.error('job-history: error getting signed URL:', e);
        }
    }
    useEffect(() => {
        if (SessionStore.isCarrier()) {
            setCarrier(SessionStore.getUser()._id);
        }
        if (SessionStore.isAdmin()) {
            setAdmin(SessionStore.getUser()._id);
        }
        document.title = t('CARGOMATIC_LOADBOARD');
        fetchAccessorialLinks();
    }, []);
    async function fetchOffers() {
        if (!isVisible) {
            return;
        }
        if (!carrier && SessionStore.isCarrier()) {
            return;
        }
        handleFetchOffersRequested();
        try {
            if (cancelTokenSource) {
                cancelRequest();
            }
            const source = axios.CancelToken.source();
            const cancelToken = source.token;
            setCancelTokenSource(source);
            const { carrier: currentCarrier } = filterMetadata, currentFilters = __rest(filterMetadata, ["carrier"]);
            const [sort, sort_dir] = sortValue.split('__');
            const { data: entity } = await getCarrierOffersV2(carrier, currentFilters, {
                page,
                per_page: PER_PAGE,
                sort,
                sort_dir,
                cancelToken,
            });
            const offers = entity.offers.map((offer) => {
                const distance = convertDistance(offer.distance, 'm', localeData.distance_units);
                const rate = offer.rate ? offer.rate : offer.price || 0;
                const rate_per_distance = distance ? rate / distance : null;
                return Object.assign(Object.assign({}, offer), { distance,
                    rate_per_distance });
            });
            if (!isEqual(currentOffers, offers)) {
                setCurrentOffers(offers);
                setPages(entity.pages || 1);
                setTotal(entity.total || 0);
            }
            handleFetchOffersSuccess();
            if (!offersLoadedFirstTime) {
                document.dispatchEvent(new CustomEvent('loadboard:offers-loaded', {
                    detail: {
                        total: entity.total,
                    },
                }));
                setOffersLoadedFirstTime(true);
            }
        }
        catch (e) {
            console.error(e);
            if (e.code !== 'ERR_CANCELED') {
                handleFetchOffersFail();
            }
        }
        finally {
            setCancelTokenSource(null);
        }
    }
    async function fetchAuthorizedDrivers() {
        if (!carrier) {
            setDrivers([]);
            setAcceptOfferDisabled(true);
            return;
        }
        const response = await getAuthorizedDrivers(carrier);
        const { entity: { drivers: carrierDrivers }, } = response;
        setDrivers(carrierDrivers.map(mapDriver));
        setAcceptOfferDisabled(carrierDrivers.length === 0);
    }
    useEffect(() => {
        fetchAuthorizedDrivers();
    }, [carrier]);
    useEffect(() => {
        fetchOffers();
    }, [page, sortValue, carrier, filterMetadata]);
    useEffect(() => {
        setSelectedOffer(null);
    }, [currentOffers]);
    useInterval(fetchOffers, REFRESH_INTERVAL);
    function handlePageClick(selectedPage) {
        setPage(selectedPage);
    }
    function handleSort(e) {
        const { target: { value }, } = e;
        setSortValue(value);
        setPage(1);
    }
    function handleOfferClick(offer) {
        setSelectedOffer(offer);
    }
    function handleDetailsClick() {
        setIsPaneOpen(true);
    }
    async function fetchMarkets() {
        const result = await getMarkets();
        const { entity: { markets }, } = result;
        const options = markets.map((market) => ({
            name: t(market === null || market === void 0 ? void 0 : market.localizationSlug),
            value: market.code,
        }));
        if (SessionStore.isAdmin()) {
            setFilters([
                {
                    key: 'carrier',
                    customerType: 'carriers',
                    type: 'customer',
                    name: 'Carrier',
                    localizationSlug: 'CARRIER',
                    placeholder: 'SELECT_CARRIER',
                },
                Object.assign(Object.assign({}, defaultFilters[0]), { options }),
                ...defaultFilters.slice(1),
            ]);
        }
        else {
            setFilters([
                Object.assign(Object.assign({}, defaultFilters[0]), { options }),
                ...defaultFilters.slice(1),
            ]);
        }
    }
    useEffect(() => {
        fetchMarkets();
    }, []);
    if (!SessionStore.isAdmin() && !SessionStore.isCarrier()) {
        return (React.createElement("div", { className: "p-4" },
            React.createElement(Alert, { title: t('REQUIRE_PERMISSION_TO_VIEW_PAGE'), type: "error" })));
    }
    function handleFilterChange(key, selected) {
        if (key === 'carrier') {
            const { name, _id } = selected;
            const updates = Object.assign(Object.assign({}, filterMetadata), { carrier: {
                    name,
                    _id,
                } });
            if (!_id) {
                handleResetOffers();
            }
            setCarrier(_id);
            setFilterMetadata(updates);
        }
        else {
            const updates = Object.assign(Object.assign({}, filterMetadata), { [key]: selected });
            setFilterMetadata(updates);
        }
    }
    function getAccessorialsLink() {
        if (selectedOffer) {
            return selectedOffer.shipment_type === 'drayage'
                ? drayageAccessorialsLink
                : shorthaulAccessorialsLink;
        }
        return null;
    }
    function renderDetails() {
        if (!selectedOffer) {
            return null;
        }
        const handleOpenModal = (value) => {
            setOpenModal(value);
        };
        return (React.createElement(OfferDetails, { accessorialsLink: getAccessorialsLink(), carrier: admin ? null : carrier, admin: admin, offer: selectedOffer, drivers: drivers, disabled: acceptOfferDisabled, onHide: handleHidePane, onAssignDriverSuccess: handleAssignDriverSuccess, handleOpenModal: handleOpenModal, openModal: openModal }));
    }
    function handleAssignDriverSuccess(driver) {
        showToast(t('OFFER_ASSIGNED_SUCCESS'), t('SUCCESSFULLY_ASSIGNED_OFFER', { driverName: driver.name }));
        if (page !== 1) {
            setPage(1);
        }
        fetchOffers();
    }
    function handleHidePane() {
        setIsPaneOpen(false);
    }
    function renderPane() {
        return (React.createElement(CargomaticPane, { container: null, show: isPaneOpen, onHide: handleHidePane, title: t('SHIPMENT_DETAILS'), location: "right", className: "sm:w-2/5 sm:max-w-none", renderBody: renderDetails }));
    }
    const mappedFilters = filters.map((filter) => {
        const key = filter.key;
        if (filter.type === 'multiselect') {
            const options = filter.options.map((option) => {
                return Object.assign(Object.assign({}, option), { selected: filterMetadata[key].includes(option.value) });
            });
            return Object.assign(Object.assign({}, filter), { options });
        }
        if (filter.type === 'range') {
            return Object.assign(Object.assign({}, filter), { value: filterMetadata[key] });
        }
        return filter;
    });
    function renderOffers() {
        if (offersLoadError) {
            return (React.createElement(Alert, { type: "error", className: "my-2" }, t('PROBLEM_LOADING_OFFERS')));
        }
        if (offersLoaded && currentOffers.length === 0) {
            return (React.createElement("div", { style: { height: 'calc(100vh - 248px)' } },
                React.createElement("p", { className: "px-4 py-5 my-0 sm:p-6" }, t('NO_OFFERS_AVAILABLE'))));
        }
        return [
            React.createElement("div", { className: classnames('overflow-y-auto'), key: 0, style: { height: 'calc(100vh - 248px)' } },
                React.createElement(CardHeaders, null),
                currentOffers.map((offer) => (React.createElement(Card, { key: offer._id, offer: offer, selected: offer._id === (selectedOffer === null || selectedOffer === void 0 ? void 0 : selectedOffer._id), accessorialsLink: offer.shipment_type === 'drayage'
                        ? drayageAccessorialsLink
                        : shorthaulAccessorialsLink, onClick: handleOfferClick, onDetailsClick: handleDetailsClick })))),
            React.createElement(Pagination, { className: "", key: 1, perPage: PER_PAGE, page: page, pages: pages, total: total, onPageClick: handlePageClick }),
        ];
    }
    return (React.createElement("div", { className: "flex flex-wrap overflow-y-hidden" },
        React.createElement("div", { className: "w-full lg:w-2/3" },
            React.createElement("div", { className: "px-4 py-5 bg-gray-100 sm:p-6" },
                React.createElement("div", { className: "flex items-center justify-between mb-2" },
                    React.createElement("h3", { className: "my-0" }, titleize(t('OFFER', { count: 2 })))),
                React.createElement("div", { className: "flex flex-wrap justify-between" },
                    React.createElement(FilterBar, { className: "mb-2", filters: mappedFilters, filterMetadata: filterMetadata, onFilterChange: handleFilterChange }),
                    React.createElement("div", { className: "flex items-center mb-2 w-52" },
                        React.createElement("select", { className: "form-select", value: sortValue, onChange: handleSort }, sortingOptions.map((option) => (React.createElement("option", { key: option.value, value: option.value }, t(option.localization_name)))))))),
            React.createElement("div", { className: "" },
                SessionStore.isCarrier() && acceptOfferDisabled && (React.createElement(Alert, { type: "info" },
                    React.createElement("a", { href: `/users/${carrier}`, target: "_blank", rel: "noreferrer nofollow" }, t('CREATE_DRIVER_TO_ACCEPT_OFFERS')))),
                renderOffers())),
        !isPaneOpen && (React.createElement("div", { className: "hidden w-full lg:block lg:w-1/3", style: { height: 'calc(100vh - 64px)' } },
            React.createElement(Map, { googleMapURL: "https://maps.googleapis.com/maps/api/js?v=3.exp&libraries=geometry,drawing,places", loadingElement: React.createElement("div", { style: { height: `100%` } }), containerElement: React.createElement("div", { style: { height: '100%' } }), mapElement: React.createElement("div", { style: { height: `100%` } }), offer: selectedOffer, defaultCenter: defaultCenterForMap }))),
        renderPane()));
};
function formatLoad(load) {
    const { t } = useTranslation();
    let type = '';
    if (load.type === 'boxes') {
        type = titleize(t('BOX', { count: (load === null || load === void 0 ? void 0 : load.quantity) || 1 }));
    }
    else if (load.type === 'pallets') {
        type = titleize(t('PALLET', { count: (load === null || load === void 0 ? void 0 : load.quantity) || 1 }));
    }
    const { length, width, height, quantity, dimensional_units } = load;
    return (React.createElement(LoadDimensionFormatter, { quantity: quantity, type: type, length: length, width: width, height: height, dimensional_units: dimensional_units }));
}
function formatTimeAgo(date) {
    return moment(date).fromNow();
}
function mapDriver(driver) {
    const { first_name: firstName, last_name: lastName } = driver;
    const first_name = (firstName || '').trim();
    const last_name = (lastName || '').trim();
    const name = `${first_name} ${last_name}`.trim();
    return Object.assign(Object.assign({}, driver), { first_name,
        last_name,
        name });
}
export default LoadBoard;
